import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CloudUpload, Loader2, Unplug, Download, CheckCircle2, AlertTriangle, HardDriveDownload } from "lucide-react";

const fmtBR = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export function OwnerDriveBackupCard() {
  const [params, setParams] = useSearchParams();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/owner/drive/status").then(({ data }) => setSt(data)).catch(() => setSt({ configured: false, connected: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const flag = params.get("drive");
    if (flag === "connected") { toast.success("Google Drive conectado"); load(); }
    else if (flag === "error") { toast.error(params.get("msg") || "Falha ao conectar o Google Drive"); }
    if (flag) {
      params.delete("drive"); params.delete("msg");
      setParams(params, { replace: true });
    }
  }, [params, setParams, load]);

  const connect = async () => {
    setBusy("c");
    try { const { data } = await api.get("/owner/drive/connect"); window.location.href = data.authorize_url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };
  const upload = async () => {
    setBusy("u");
    try { await api.post("/owner/drive/upload-now"); toast.success("Backup enviado ao seu Drive"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const disconnect = async () => {
    if (!window.confirm("Desconectar o Google Drive? O backup diário vai parar; nada é apagado do Drive.")) { return; }
    setBusy("d");
    try { await api.post("/owner/drive/disconnect"); toast.success("Drive desconectado"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const download = async () => {
    setBusy("x");
    try {
      const { data, headers } = await api.get("/owner/drive/xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      const cd = headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.href = url; a.download = m ? m[1] : "autooficina.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const last = st?.last_upload;
  const connected = st?.connected;

  return (
    <div data-testid="owner-drive-card" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7 space-y-3">
      <div className="flex items-center gap-2">
        <CloudUpload className="h-4 w-4 text-sky-400" />
        <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Backup no meu Google Drive</h2>
      </div>
      <p className="text-sm text-zinc-400">
        Opcional. Toda noite enviamos uma <strong>planilha Excel</strong> com todos os dados da sua oficina (sem fotos e vídeos) para a pasta <strong>"AutoOficina Backup"</strong> do seu Drive. Guardamos as 30 cópias mais novas e apagamos as mais antigas. Se não conectar, nada muda.
      </p>
      {!st ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>
      ) : !st.configured ? (
        <p className="text-sm text-amber-300">OAuth do Google não configurado no servidor.</p>
      ) : !connected ? (
        <div className="flex flex-wrap gap-2">
          <Button data-testid="owner-drive-connect" onClick={connect} disabled={busy === "c"} className="bg-sky-600 hover:bg-sky-500 text-white font-bold uppercase">
            {busy === "c" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-2" />} Conectar meu Google Drive
          </Button>
          <Button data-testid="owner-drive-download" variant="outline" onClick={download} disabled={busy === "x"} className="bg-zinc-900 border-zinc-800">
            {busy === "x" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <HardDriveDownload className="h-4 w-4 mr-2" />} Baixar planilha agora
          </Button>
        </div>
      ) : (
        <>
          <div className="text-sm text-zinc-200">
            Conectado como <span className="font-semibold">{st.email}</span> {st.connected_at && <span className="text-zinc-500">desde {fmtBR(st.connected_at)}</span>}
          </div>
          <div data-testid="owner-drive-last" className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-sm">
            {!last ? (
              <span className="text-zinc-500">Nenhum envio ainda. O primeiro acontece hoje à noite ou use "Enviar agora".</span>
            ) : last.status === "ok" ? (
              <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Último envio {fmtBR(last.at)} · {last.filename}</span>
            ) : (
              <span className="text-red-300 inline-flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Falhou em {fmtBR(last.at)} — {last.error}</span>
            )}
            {st.fail_streak >= 2 && <div className="mt-1 text-xs text-red-300">Avisei o dono por e-mail (falhas seguidas: {st.fail_streak}).</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="owner-drive-upload" onClick={upload} disabled={busy === "u"} className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
              {busy === "u" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-2" />} Enviar agora
            </Button>
            <Button data-testid="owner-drive-download" variant="outline" onClick={download} disabled={busy === "x"} className="bg-zinc-900 border-zinc-800">
              {busy === "x" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <HardDriveDownload className="h-4 w-4 mr-2" />} Baixar planilha agora
            </Button>
            <Button data-testid="owner-drive-disconnect" variant="outline" onClick={disconnect} disabled={busy === "d"} className="bg-zinc-900 border-zinc-800 text-red-300 hover:text-red-200">
              {busy === "d" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unplug className="h-4 w-4 mr-2" />} Desconectar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
